import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { Markdown } from "tiptap-markdown";
import { Icon, type IconName } from "@/components/ui/Icon";
import { MarkdownMention, makeMentionSuggestion } from "@/components/ui/mentionSuggestion";
import { HighlightMark } from "@/components/ui/highlightMark";
import { MemoFidelity, MemoImage } from "@/components/ui/memoFidelityExtension";
import { MemoListKeys } from "@/components/ui/memoListKeys";
import { isBlankDoc } from "@/lib/editorBlank";
import type { MentionItem } from "@/lib/types";
import { clipboardHasTable } from "@/lib/clipboard";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";

// WYSIWYG comment editor. Edits as rich text but serializes to Markdown
// (tiptap-markdown) so storage stays backward-compatible with the existing
// markdown comments + the image/file attachment flow. Rendering is handled by
// MarkdownContent. File paste/drop is routed out to the caller's upload flow
// rather than embedded inline.

interface Props {
  value: string;
  onChange: (markdown: string) => void;
  onSubmit?: () => void;
  onFiles?: (files: File[]) => void;
  /** When set, pasted/dropped files are uploaded and inserted at the cursor as
   * markdown (image → `![]()`, other → `[]()`) instead of being handed to
   * `onFiles`. Used by replies/edits and long-text cells that have no separate
   * attachment strip. The promise's `isImage` decides the markdown shape. */
  onUploadFile?: (file: File) => Promise<{ name: string; url: string; isImage: boolean }>;
  /** Fired when the editor loses focus, with the current markdown — lets a cell
   * editor commit-on-blur.
   *
   * `blank` = the writer put nothing in the document (`lib/editorBlank`). Only the
   * memo's ghost-row cleanup needs it; consumers that don't care simply declare
   * one parameter. It is NOT derivable from `markdown` — an empty bullet
   * serializes to "-". */
  onBlur?: (markdown: string, blank: boolean) => void;
  placeholder?: string;
  /** Candidates for the @-mention suggestion list (workspace members). */
  mentionItems?: MentionItem[];
  /** Memo entries: Ctrl/Cmd+Enter COMMITS this entry and closes its editor —
   * it never opens a new bullet. Plain Enter is left to TipTap so the writer
   * stays inside the text field (a minute is a paragraph, not one bullet per
   * line). Absent for comments/cells → they use onSubmit/onBlur instead.
   *
   * Point this at the SAME handler as `onBlur`, not at a bare commit: an entry
   * that was opened via "+ Eintrag" and never filled must be deleted rather
   * than left as a ghost row. It carries the same `(markdown, blank)` as `onBlur`
   * so both CAN be the same function — and reads them off the live document
   * rather than the consumer's draft state. */
  onFinish?: (markdown: string, blank: boolean) => void;
  /** Memo fast-flow: Backspace on an empty editor deletes the bullet + focuses
   * the previous one. */
  onEmptyBackspace?: () => void;
  /** Focus the editor on mount (e.g. a freshly-opened memo bullet). */
  autoFocus?: boolean;
  /** Prime the bullet with a leading `@` and open the mention picker on mount
   * (the "+ Aufgabe" flow) so the entry starts as an assignment → a to-do. */
  autoMention?: boolean;
  /** `"memo"`: a Protokoll entry. Adds the memo-only extensions (MemoFidelity:
   * blank lines survive saving, empty bullets are not stored; MemoListKeys:
   * Word-like Backspace/Tab in lists, Tab never leaves the editor) and the
   * `flow-rte-memo` class on the content element. Read once at mount: the
   * extension list is fixed for the life of an editor. Every other consumer
   * leaves it unset and behaves exactly as before. */
  variant?: "memo";
}

/** A Protokoll entry's document as it was loaded, and the Markdown it was loaded from. */
interface LoadedBody {
  editor: Editor;
  doc: ProseMirrorNode;
  markdown: string;
}

/**
 * The Markdown the editor reports. For a Protokoll entry whose document still equals
 * the one it loaded, that is the loaded Markdown, byte for byte; otherwise (and for
 * every other editor, which passes no `loaded`) it is the serializer's output.
 *
 * 🔴 MemoFidelity normalizes what a Protokoll entry saves (an empty bullet is dropped,
 * a stored list renumbers), so an old body serialized again without a single keystroke
 * can differ from what is stored, and MemoEntryRow PATCHes any difference: a click into
 * a point and out again would rewrite it, even in a finalized Protokoll, and log an
 * "edited" line in the Änderungsprotokoll under the clicker's name. The first real edit
 * stores the normalized body. `eq`, not identity: an edit that is undone is no edit.
 */
function reportedMarkdown(ed: Editor, loaded: LoadedBody | null): string {
  if (loaded && ed.state.doc.eq(loaded.doc)) return loaded.markdown;
  return ed.storage.markdown.getMarkdown();
}

// Collect pasteable/droppable files from a clipboard or drag payload. A pasted
// screenshot arrives via `items` (kind "file") and is frequently absent from
// `files` (or carries a useless "image.png" name), so scan both, dedupe by
// name+size+type, and synthesize an extensioned name when missing so the
// server's extension allowlist accepts it.
function collectFiles(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const raw: File[] = [];
  const seen = new Set<string>();
  const consider = (f: File | null) => {
    if (!f || f.size === 0) return;
    const key = `${f.name}:${f.size}:${f.type}`;
    if (seen.has(key)) return;
    seen.add(key);
    raw.push(f);
  };
  for (const f of Array.from(dt.files)) consider(f);
  for (const item of Array.from(dt.items)) {
    if (item.kind === "file" && item.type.startsWith("image/")) consider(item.getAsFile());
  }
  return raw.map((f, i) =>
    !f.name || f.name === "image.png"
      ? new File([f], `pasted-${i + 1}.${f.type.split("/")[1] || "png"}`, { type: f.type })
      : f,
  );
}

export function RichTextEditor({
  value,
  onChange,
  onSubmit,
  onFiles,
  onUploadFile,
  onBlur,
  placeholder,
  mentionItems,
  onFinish,
  onEmptyBackspace,
  autoFocus,
  autoMention,
  variant,
}: Props) {
  // Fixed at mount, like the extension list built from it.
  const [memoVariant] = useState(() => variant === "memo");
  // A pasted payload that is BOTH an image and a table, held until the writer
  // picks one. Captured synchronously in handlePaste because `clipboardData` is
  // only readable during the event.
  const [pasteChoice, setPasteChoice] = useState<{ files: File[]; html: string } | null>(null);
  // Read by the `blur` handler, which is registered once against the editor and
  // would otherwise close over a stale `pasteChoice`.
  const pasteChoiceOpenRef = useRef(false);
  pasteChoiceOpenRef.current = pasteChoice !== null;
  // Uploads that have been started but whose image/link node has not landed yet.
  // While this is > 0 the document under-reports its content — see editorBusy().
  const uploadsInFlightRef = useRef(0);

  // Insert the leading `@` at most once (the "+ Aufgabe" prime), never on a
  // re-render.
  const autoMentionDoneRef = useRef(false);
  // The editor is created once; callbacks are read through refs so the (single)
  // editor instance always sees the latest props without re-instantiating.
  const mentionItemsRef = useRef<MentionItem[]>(mentionItems ?? []);
  mentionItemsRef.current = mentionItems ?? [];
  // True while the @-mention suggestion popup is open — the memo fast-flow Enter
  // must defer to the popup (which uses Enter to select), or selecting a mention
  // also commits+advances the bullet with a stale (mention-less) draft → no task.
  const mentionOpenRef = useRef(false);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;
  const onUploadFileRef = useRef(onUploadFile);
  onUploadFileRef.current = onUploadFile;
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;
  const onEmptyBackspaceRef = useRef(onEmptyBackspace);
  onEmptyBackspaceRef.current = onEmptyBackspace;
  const editorRef = useRef<Editor | null>(null);
  // Protokoll entries only (see reportedMarkdown): set when the editor is created and
  // whenever the value-sync effect below loads a new value. Null for every other editor.
  const loadedRef = useRef<LoadedBody | null>(null);

  // Route captured paste/drop files: upload+insert at the cursor when an
  // onUploadFile is provided (replies/edits/cells), else hand to onFiles (the
  // main composer's staged-attachment strip). Returns whether the files were
  // handled (so the editor can consume the event).
  function handleFiles(files: File[]): boolean {
    if (onUploadFileRef.current) {
      // 🔴 Counted SYNCHRONOUSLY, before the upload's first await, so a blur or
      // Ctrl+Enter arriving in the next tick already sees the editor as busy.
      uploadsInFlightRef.current += 1;
      void uploadAndInsert(files).finally(() => {
        uploadsInFlightRef.current -= 1;
        if (uploadsInFlightRef.current === 0) drainDeferredExit();
      });
      return true;
    }
    if (onFilesRef.current) {
      onFilesRef.current(files);
      return true;
    }
    return false;
  }

  async function uploadAndInsert(files: File[]) {
    const ed = editorRef.current;
    const upload = onUploadFileRef.current;
    if (!ed || !upload) return;
    for (const file of files) {
      try {
        const { name, url, isImage } = await upload(file);
        // The editor can be torn down mid-upload (the row left edit mode). Writing
        // to a destroyed editor throws and would strand the uploaded file.
        if (ed.isDestroyed) return;
        // Images insert as a real image node (renders inline, not raw ![](url)
        // text); other files insert as a markdown link.
        if (isImage) {
          ed.chain().focus().setImage({ src: url, alt: name }).run();
        } else {
          ed.chain().focus().insertContent(`[${name}](${url}) `).run();
        }
      } catch {
        // upload() owns its own error toast.
      }
    }
  }

  // 🔴 An upload in flight means the document is EMPTY even though the writer has
  // already committed a screenshot to it — there is no placeholder node. Blur or
  // Ctrl+Enter in that window would report `blank: true` and the memo's ghost-row
  // cleanup would DELETE the entry, orphaning the file that is still uploading.
  // (The paste chooser's `insertAsImage` makes this worse: it clears the chooser —
  // releasing `pasteChoiceOpenRef` — the moment the upload STARTS.)
  //
  // So an exit requested while uploading is DEFERRED, not dropped: the writer has
  // not finished, they are waiting on us. When the last upload settles, the exit
  // runs against the live document — which by then holds the image.
  const exitDeferredRef = useRef(false);
  function drainDeferredExit() {
    if (!exitDeferredRef.current) return;
    exitDeferredRef.current = false;
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    const md = reportedMarkdown(ed, loadedRef.current);
    const blank = isBlankDoc(ed.state.doc);
    // Memo entries finish; cell editors commit on blur. Same precedence as the
    // Ctrl+Enter branch below.
    if (onFinishRef.current) onFinishRef.current(md, blank);
    else onBlurRef.current?.(md, blank);
  }
  /** The writer is mid-action: a paste chooser is open, or an upload is landing. */
  function editorBusy(): boolean {
    return pasteChoiceOpenRef.current || uploadsInFlightRef.current > 0;
  }

  // The last markdown WE emitted. Markdown does not round-trip identically
  // (serialization re-normalizes escapes / list markers / trailing space), so
  // the value echoed back by the parent rarely equals the editor's freshly
  // re-derived markdown. Tracking our own emission lets the sync effect tell an
  // echo (skip) from a real external reset (apply) — without it, setContent ran
  // on every keystroke and yanked the caret to the document end.
  const lastEmittedRef = useRef<string>(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
      // Native tables: paste an Excel/Sheets selection → an editable grid.
      // Serializes to a GFM markdown table when simple, else to HTML (html:true
      // below) so merged/multi-line cells aren't lost — the display renders both
      // (remark-gfm for GFM, sanitized rehype-raw for the HTML).
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      // Renders ![](url) as an inline <img> while editing (round-trips to
      // markdown via tiptap-markdown) so stored screenshots show as images, not
      // raw markdown text — and uploads insert a visible image node.
      // Protokoll entries: a screenshot closes its block, so a following blank line
      // is stored as a line of its own (MemoImage).
      (memoVariant ? MemoImage : Image).configure({ inline: false, allowBase64: false }),
      // breaks: a single newline is a hard line break (not collapsed to a space),
      // matching how plain long-text notes were written + rendered before the
      // WYSIWYG migration. remark-breaks mirrors this on the display side.
      // html:true so a complex table (merged / multi-line cells) serializes to
      // HTML instead of the lossy "[table]" placeholder; the display sanitizes
      // it with rehype-sanitize, so no XSS surface is opened.
      Markdown.configure({ html: true, linkify: true, transformPastedText: true, breaks: true }),
      HighlightMark,
      MarkdownMention.configure({
        HTMLAttributes: { class: "flow-mention" },
        suggestion: makeMentionSuggestion(
          () => mentionItemsRef.current,
          (open) => {
            mentionOpenRef.current = open;
          },
        ),
      }),
      ...(memoVariant ? [MemoFidelity, MemoListKeys] : []),
    ],
    // Trim a trailing blank block on load: a stored body ending in a soft break
    // / newline would otherwise materialize as an empty trailing line, and
    // focus("end") would drop the caret onto that phantom row (below the last
    // real line) instead of at the end of the actual text. Trailing whitespace
    // is never meaningful, so this is safe for every editor surface.
    content: value.replace(/\s+$/, ""),
    editorProps: {
      attributes: { class: memoVariant ? "flow-rte-content flow-rte-memo" : "flow-rte-content" },
      handleKeyDown: (_view, event) => {
        // Ctrl/Cmd+Enter is the ONLY key that leaves the editor.
        //
        // Plain Enter and Shift+Enter are deliberately NOT intercepted: they
        // fall through to TipTap (new paragraph / new list item, and hard break
        // respectively), so a minute-taker typing a multi-line point stays in
        // the text field instead of being thrown into a new bullet — which,
        // combined with the backend insert clamp, teleported the caret to the
        // end of the Zwischenabschnitt. Memo to-dos are created via the
        // "+ Aufgabe" button (which primes a leading @ on a fresh entry), so
        // nothing depends on one-bullet-per-entry any more.
        //
        // 🔴 The mention guard is load-bearing: while the @-popup is open it
        // owns Enter. Without `!mentionOpenRef.current` a Ctrl+Enter mid-mention
        // would submit/finish a draft whose mention hasn't been inserted yet —
        // the stale-draft bug class that made "+ Aufgabe" spawn no to-do.
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
          if (mentionOpenRef.current) return true; // swallow; the popup owns Enter
          event.preventDefault();
          // A screenshot is still uploading: the doc looks empty, so finishing now
          // would delete the entry. Remember the intent and run it once it lands.
          if (editorBusy()) {
            exitDeferredRef.current = true;
            return true;
          }
          // Comments submit; memo entries finish (commit + close, never advance);
          // cell editors (onBlur set, no onSubmit/onFinish) commit by blurring,
          // which fires their onBlur save. Order matters.
          if (onSubmitRef.current) onSubmitRef.current();
          else if (onFinishRef.current) {
            const ed = editorRef.current;
            if (ed)
              onFinishRef.current(
                reportedMarkdown(ed, loadedRef.current),
                isBlankDoc(ed.state.doc),
              );
          } else if (onBlurRef.current) editorRef.current?.commands.blur();
          return true;
        }
        // An empty point deletes itself on Backspace, except right after an input rule:
        // typing "- " into an empty point leaves a document that is one empty bullet,
        // and Backspace there brings the typed characters back (TipTap's standard,
        // done by the keymaps), instead of throwing the point away.
        if (
          event.key === "Backspace" &&
          onEmptyBackspaceRef.current &&
          editorRef.current?.isEmpty &&
          !editorRef.current.can().undoInputRule()
        ) {
          event.preventDefault();
          onEmptyBackspaceRef.current();
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const files = collectFiles(event.clipboardData);
        const html = event.clipboardData?.getData("text/html") ?? "";
        // Excel hands us a bitmap AND a <table>. Both are legitimate wishes — a
        // grid you can edit, or a picture of the formatting — so ask instead of
        // silently preferring the image (which is what consuming the file here
        // used to do). Only ambiguous payloads prompt: a screenshot has no table,
        // a OneNote table has no bitmap, and both keep their one-step behaviour.
        if (files.length > 0 && clipboardHasTable(html)) {
          setPasteChoice({ files, html });
          return true;
        }
        if (files.length === 0) return false;
        return handleFiles(files);
      },
      handleDrop: (_view, event) => {
        const files = collectFiles((event as DragEvent).dataTransfer);
        if (files.length === 0) return false;
        event.preventDefault();
        return handleFiles(files);
      },
    },
    onUpdate: ({ editor }) => {
      const md = reportedMarkdown(editor, loadedRef.current);
      lastEmittedRef.current = md;
      onChange(md);
    },
  });

  editorRef.current = editor;
  // The editor is created during this render, from this render's `value`, and nothing
  // has typed into it yet (the "+ Aufgabe" prime runs in an effect).
  if (memoVariant && editor && loadedRef.current?.editor !== editor) {
    loadedRef.current = { editor, doc: editor.state.doc, markdown: value.replace(/\s+$/, "") };
  }

  // Commit-on-blur for cell editors (onBlur set). Toolbar buttons preventDefault
  // on mousedown so they don't blur; a genuine focus-out fires this.
  //
  // 🔴 The paste chooser SUSPENDS this. Its primary button is autoFocused for
  // keyboard use, which moves focus out of the contenteditable and makes TipTap
  // emit `blur`. On a memo entry that blur runs `exitEditor` → `onStopEdit` →
  // the editor unmounts → the chooser (component state) is destroyed before the
  // writer can pick, and on a fresh "+ Eintrag" it takes the ghost-delete branch,
  // so pasting Excel would silently delete the bullet. Focus moving into our own
  // chooser is not the writer leaving the field.
  useEffect(() => {
    if (!editor) return;
    const handler = () => {
      if (editorBusy()) {
        // Focus left while an upload was landing. Don't drop the exit (the writer
        // did mean to leave) and don't run it now (the doc is still empty and the
        // entry would be ghost-deleted) — defer it until the image is in.
        if (uploadsInFlightRef.current > 0) exitDeferredRef.current = true;
        return;
      }
      onBlurRef.current?.(
        reportedMarkdown(editor, loadedRef.current),
        isBlankDoc(editor.state.doc),
      );
    };
    editor.on("blur", handler);
    return () => {
      editor.off("blur", handler);
    };
  }, [editor]);

  // Focus on mount when requested (memo bullet opened via click or Enter-chain).
  // For the "+ Aufgabe" prime, also type a leading `@` (once) so the mention
  // picker opens and the bullet starts as an assignment → a to-do on commit.
  useEffect(() => {
    if (!autoFocus || !editor) return;
    editor.commands.focus("end");
    if (autoMention && !autoMentionDoneRef.current) {
      autoMentionDoneRef.current = true;
      editor.chain().focus("end").insertContent("@").run();
    }
  }, [autoFocus, autoMention, editor]);

  // External value resets (e.g. the composer clearing after send) — sync the
  // editor without emitting an update (which would loop back into onChange).
  // Skip echoes of our own onUpdate: if `value` equals what we last emitted it
  // is the round-trip of the user's own typing, and calling setContent would
  // reset the selection to the document end on every keystroke.
  useEffect(() => {
    if (!editor) return;
    if (value === lastEmittedRef.current) return;
    const current = reportedMarkdown(editor, loadedRef.current);
    if (value === current) return;
    editor.commands.setContent(value, false);
    lastEmittedRef.current = value;
    // A Protokoll entry has just loaded a new body (MemoEntryRow re-seeds its draft
    // when an edit session opens): that body is what an unedited document reports.
    if (loadedRef.current) {
      loadedRef.current = { editor, doc: editor.state.doc, markdown: value.replace(/\s+$/, "") };
    }
  }, [value, editor]);

  // Resolve the ambiguous paste. Inserting the HTML goes through TipTap's schema
  // parser, so only nodes the editor knows survive — a <script> in the clipboard
  // HTML cannot reach the document, exactly as with a normal paste.
  // Each path clears `pasteChoice` LAST and hands focus back to the editor, so the
  // suspended blur handler resumes with the caret where the writer expects it.
  const insertAsTable = () => {
    if (!pasteChoice) return;
    // 🔴 `preserveWhitespace: false` is load-bearing. Excel pretty-prints its
    // clipboard HTML, so there is a whitespace text node before every <tr> and
    // every <td>. TipTap's default parse keeps them, ProseMirror wraps each into
    // a cell/row, and a 2x3 range lands as a 4x6 grid with 18 empty cells.
    // Measured: without it rows=6 cells=24 empty=18; with it rows=3 cells=6.
    editorRef.current
      ?.chain()
      .focus()
      .insertContent(pasteChoice.html, { parseOptions: { preserveWhitespace: false } })
      .run();
    setPasteChoice(null);
  };
  const insertAsImage = () => {
    if (!pasteChoice) return;
    handleFiles(pasteChoice.files);
    editorRef.current?.commands.focus();
    setPasteChoice(null);
  };
  const cancelPaste = () => {
    editorRef.current?.commands.focus();
    setPasteChoice(null);
  };

  return (
    <div>
      {pasteChoice ? (
        // Deliberately INLINE, not a portaled dialog: this editor also lives
        // inside the long-text cell's popover, and a portaled modal there eats
        // keystrokes (the Phase 4 focus-trap footgun).
        <div
          role="group"
          aria-label={t.pasteChoiceTitle}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              cancelPaste();
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 8,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid var(--hairline)",
            background: "var(--surface-soft)",
          }}
        >
          <span style={{ fontSize: 12.5, color: "var(--ink-2)" }}>{t.pasteChoiceTitle}</span>
          {/* onMouseDown preventDefault mirrors the toolbar: a click must not blur
              the editor. autoFocus keeps the chooser reachable by keyboard; the
              blur it causes is neutralised by pasteChoiceOpenRef. */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertAsTable}
            style={pasteChoiceBtn(true)}
            autoFocus
          >
            {t.pasteAsTable}
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={insertAsImage}
            style={pasteChoiceBtn(false)}
          >
            {t.pasteAsImage}
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={cancelPaste}
            style={{
              ...pasteChoiceBtn(false),
              border: "none",
              color: "var(--ink-3)",
            }}
          >
            {t.cancel}
          </button>
        </div>
      ) : null}
      {editor ? <Toolbar editor={editor} /> : null}
      <div style={{ position: "relative" }}>
        <EditorContent editor={editor} />
        {/* isEmpty is true even for an empty bullet/heading (no text content), which
            would leave the placeholder overlapping the list marker. Only show it
            while the doc is still the default empty paragraph. */}
        {editor?.isEmpty &&
        placeholder &&
        editor.state.doc.firstChild?.type.name === "paragraph" ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              fontSize: 13.5,
              lineHeight: 1.5,
              color: "var(--ink-4)",
              pointerEvents: "none",
            }}
          >
            {placeholder}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Both grounds are tokens (or both fixed), so the button stays legible in light
// and dark — never a flipping var() paired with a hardcoded #fff.
function pasteChoiceBtn(primary: boolean): CSSProperties {
  return {
    fontSize: 12.5,
    fontWeight: 550,
    padding: "4px 10px",
    borderRadius: 6,
    cursor: "pointer",
    border: primary ? "none" : "1px solid var(--hairline-strong)",
    background: primary ? BRAND.blue : "transparent",
    color: primary ? "#fff" : "var(--ink-2)",
  };
}

const TOOLS: {
  icon: IconName;
  label: string;
  isActive: (e: Editor) => boolean;
  run: (e: Editor) => void;
}[] = [
  {
    icon: "bold",
    label: "Fett",
    isActive: (e) => e.isActive("bold"),
    run: (e) => e.chain().focus().toggleBold().run(),
  },
  {
    icon: "italic",
    label: "Kursiv",
    isActive: (e) => e.isActive("italic"),
    run: (e) => e.chain().focus().toggleItalic().run(),
  },
  {
    icon: "code",
    label: "Code",
    isActive: (e) => e.isActive("code"),
    run: (e) => e.chain().focus().toggleCode().run(),
  },
  {
    // LogicLine Orange text highlight. Fixed ink-on-paper colours in both themes
    // (see .flow-rte-content mark / .flow-md mark in index.css).
    icon: "highlighter",
    label: "Hervorheben (Strg+Umschalt+H)",
    isActive: (e) => e.isActive("highlight"),
    run: (e) => e.chain().focus().toggleHighlight().run(),
  },
  {
    icon: "listBullet",
    label: "Liste",
    isActive: (e) => e.isActive("bulletList"),
    run: (e) => e.chain().focus().toggleBulletList().run(),
  },
  {
    icon: "listOrdered",
    label: "Nummerierte Liste",
    isActive: (e) => e.isActive("orderedList"),
    run: (e) => e.chain().focus().toggleOrderedList().run(),
  },
  {
    // Indent / outdent a list item (also bound to Tab / Shift+Tab). No-ops
    // outside a list, so it's a nested-bullet control.
    icon: "indent",
    label: "Einrücken (Tab)",
    isActive: () => false,
    run: (e) => e.chain().focus().sinkListItem("listItem").run(),
  },
  {
    icon: "outdent",
    label: "Ausrücken (Umschalt+Tab)",
    isActive: () => false,
    run: (e) => e.chain().focus().liftListItem("listItem").run(),
  },
  {
    icon: "quote",
    label: "Zitat",
    isActive: (e) => e.isActive("blockquote"),
    run: (e) => e.chain().focus().toggleBlockquote().run(),
  },
];

function Toolbar({ editor }: { editor: Editor }) {
  const btnStyle = (active: boolean): CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "none",
    // `--brand-blue-wash` / `--brand-blue` NEVER EXISTED (the real token is
    // `--color-brand-blue`), so both var() calls always fell through to their
    // hardcoded fallbacks — an active toolbar button was dark-blue-on-dark.
    background: active ? "var(--accent-soft)" : "transparent",
    color: active ? "var(--accent)" : "var(--ink-3)",
    cursor: "pointer",
  });
  const setLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt(t.rteLinkPrompt, prev ?? "https://");
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };
  return (
    <div
      // 🔴 The CONTAINER must preventDefault, not just the buttons. Every button below
      // already does — but the strip they sit in is the full width of the entry, and the
      // buttons occupy only its left third. A mousedown on the empty remainder moved focus
      // to <body>, the editor emitted `blur`, and a memo entry COMMITS AND CLOSES on blur
      // (MemoEntryRow.exitEditor). So clicking the strip shut the editor mid-edit.
      //
      // This bites hardest exactly where it is least forgivable — live, in presentation
      // mode, in front of the room. Opening an editor INSERTS this toolbar above the text
      // and pushes the text down by its own height, so the spot the writer just clicked (her
      // text) is now the toolbar. Her next click, to place the caret, lands on it. The
      // presentation zoom (1.34) makes the strip ~47px tall and the row ~1000px wide, so
      // there are several hundred px of dead zone directly above the line she is editing.
      // Reported live: "when she wanted to set the cursor at the right position, it closes."
      //
      // preventDefault on MOUSEDOWN, not a blur guard: this stops focus from ever leaving
      // the contenteditable, which is the only thing that keeps the caret where it was.
      onMouseDown={(e) => e.preventDefault()}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        paddingBottom: 6,
        marginBottom: 6,
        borderBottom: "1px solid var(--hairline-soft)",
      }}
    >
      {TOOLS.map((tool) => (
        <button
          key={tool.icon}
          type="button"
          title={tool.label}
          aria-label={tool.label}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => tool.run(editor)}
          style={btnStyle(tool.isActive(editor))}
        >
          <Icon name={tool.icon} size={15} />
        </button>
      ))}
      <button
        type="button"
        title={t.rteLink}
        aria-label={t.rteLink}
        onMouseDown={(e) => e.preventDefault()}
        onClick={setLink}
        style={btnStyle(editor.isActive("link"))}
      >
        <Icon name="link" size={15} />
      </button>
      <button
        type="button"
        title="Tabelle einfügen"
        aria-label="Tabelle einfügen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        }
        style={btnStyle(editor.isActive("table"))}
      >
        <Icon name="table" size={15} />
      </button>
      {editor.isActive("table") ? (
        <>
          <span style={{ width: 1, height: 16, background: "var(--hairline)", margin: "0 3px" }} />
          {(
            [
              ["+ Zeile", () => editor.chain().focus().addRowAfter().run()],
              ["+ Spalte", () => editor.chain().focus().addColumnAfter().run()],
              ["− Zeile", () => editor.chain().focus().deleteRow().run()],
              ["− Spalte", () => editor.chain().focus().deleteColumn().run()],
              ["Tabelle ✕", () => editor.chain().focus().deleteTable().run()],
            ] as const
          ).map(([label, run]) => (
            <button
              key={label}
              type="button"
              title={label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={run}
              style={{
                border: "none",
                background: "transparent",
                color: "var(--ink-3)",
                cursor: "pointer",
                fontSize: 12,
                padding: "4px 6px",
                borderRadius: 6,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          ))}
        </>
      ) : null}
    </div>
  );
}
